import { cn } from '@/lib/utils';
import { useAdherence } from '@/hooks/useClosedLoopAnalytics';
import { CheckCircle, Flame, Target } from 'lucide-react';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface AdherenceCardProps {
  className?: string;
}

function DonutRing({ value, size = 56, strokeWidth = 5, color }: { value: number; size?: number; strokeWidth?: number; color: string }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-700"
      />
    </svg>
  );
}

function MiniBar({ value, maxValue }: { value: number; maxValue: number }) {
  const pct = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div className="h-4 flex-1 rounded-sm bg-white/[0.04] overflow-hidden">
      <div
        className="h-full rounded-sm transition-all duration-500"
        style={{
          width: `${pct}%`,
          backgroundColor: pct >= 80 ? 'hsl(170, 80%, 45%)' : pct >= 50 ? 'hsl(35, 85%, 55%)' : 'hsl(15, 85%, 55%)',
        }}
      />
    </div>
  );
}

export function AdherenceCard({ className }: AdherenceCardProps) {
  const { data, isLoading } = useAdherence(12);

  if (isLoading) {
    return (
      <div className={cn('p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center animate-pulse">LOADING ADHERENCE...</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const completionPct = Math.round((data.overall_completion_rate ?? 0) * 100);
  const qualityPct = Math.round((data.overall_quality_rate ?? 0) * 100);
  const weeklyRates = data.weekly_completion_rates ?? [];

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          PLAN ADHERENCE
        </span>
        <MetricInfoButton metricKey="adherence" />
        <span className="ml-auto text-[9px] font-mono text-white/20 uppercase tracking-wider">
          {data.weeks_analyzed ?? 0}W
        </span>
      </div>

      {/* Donut rings + stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Completion */}
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <DonutRing value={completionPct} color="hsl(170, 80%, 45%)" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-mono tabular-nums text-foreground">{completionPct}%</span>
            </div>
          </div>
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            Completion <MetricInfoButton metricKey="completionRate" />
          </span>
        </div>

        {/* Quality */}
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <DonutRing value={qualityPct} color="hsl(35, 85%, 55%)" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-mono tabular-nums text-foreground">{qualityPct}%</span>
            </div>
          </div>
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            Quality <MetricInfoButton metricKey="qualityScore" />
          </span>
        </div>
      </div>

      {/* Session counts */}
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div>
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <CheckCircle className="w-3 h-3 text-emerald-400" />
            <span className="text-sm font-mono tabular-nums text-foreground">{data.sessions_completed ?? 0}</span>
          </div>
          <span className="text-[8px] font-mono text-muted-foreground uppercase tracking-wider">Done</span>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Target className="w-3 h-3 text-muted-foreground" />
            <span className="text-sm font-mono tabular-nums text-foreground">{data.sessions_planned ?? 0}</span>
          </div>
          <span className="text-[8px] font-mono text-muted-foreground uppercase tracking-wider">Planned</span>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Flame className="w-3 h-3 text-amber-400" />
            <span className="text-sm font-mono tabular-nums text-foreground">{data.current_streak_weeks ?? 0}</span>
          </div>
          <span className="text-[8px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            Streak <MetricInfoButton metricKey="streak" />
          </span>
        </div>
      </div>

      {/* Weekly bars */}
      {weeklyRates.length > 0 && (
        <div>
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider mb-1.5 block">
            Weekly Completion
          </span>
          <div className="flex items-end gap-0.5">
            {weeklyRates.map((rate, i) => (
              <MiniBar key={i} value={rate * 100} maxValue={100} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
